import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { CheckCircle2, LogOut as LogOutIcon } from "lucide-react";

const STATUSES = ["all", "confirmed", "checked_in", "checked_out", "pending"];

function statusPill(s) {
  const cls = {
    checked_in: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
    checked_out: "bg-white/5 text-white/50 border-white/10",
    confirmed: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20",
    pending: "bg-orange-500/10 text-orange-300 border-orange-500/20",
  }[s] || "bg-white/5 text-white/60 border-white/10";
  return <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${cls}`}>{s.replace("_", " ")}</span>;
}

export default function Reservations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/reservations", { params: { limit: 200 } });
      setItems(data);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => filter === "all" ? items : items.filter(x => x.status === filter), [items, filter]);

  async function checkIn(id) {
    try { await api.post(`/reservations/${id}/check-in`); toast.success("Guest checked in"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Check-in failed"); }
  }
  async function checkOut(id) {
    try { await api.post(`/reservations/${id}/check-out`); toast.success("Guest checked out"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Check-out failed"); }
  }

  return (
    <div className="space-y-8" data-testid="reservations-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Reservations</div>
        <h1 className="font-display text-3xl md:text-4xl">All bookings</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs uppercase tracking-widest px-3 py-2 rounded-md border transition ${
              filter === s ? "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30" : "bg-transparent text-white/60 border-white/10 hover:text-white"
            }`}
            data-testid={`filter-${s}`}
          >{s.replace("_", " ")}</button>
        ))}
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl overflow-hidden" data-testid="reservations-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/5">
                <th className="px-5 py-4">Guest</th>
                <th className="px-5 py-4">Room</th>
                <th className="px-5 py-4">Check-in</th>
                <th className="px-5 py-4">Check-out</th>
                <th className="px-5 py-4">Channel</th>
                <th className="px-5 py-4">Amount</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center text-white/50 py-12">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center text-white/50 py-12">No reservations.</td></tr>}
              {!loading && filtered.map((r) => (
                <tr key={r.id} className="border-b border-white/5 last:border-none hover:bg-[#1A2235] transition" data-testid={`reservation-row-${r.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-medium">{r.guest_name}</div>
                    <div className="text-xs text-white/50">{r.guests_count} guest{r.guests_count > 1 ? "s" : ""}</div>
                  </td>
                  <td className="px-5 py-4"><div>{r.room_number}</div><div className="text-xs text-white/50">{r.room_type}</div></td>
                  <td className="px-5 py-4 text-white/80">{new Date(r.check_in).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="px-5 py-4 text-white/80">{new Date(r.check_out).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="px-5 py-4 text-white/80">{r.channel}</td>
                  <td className="px-5 py-4 text-white/80">₹{Math.round(r.total_amount).toLocaleString("en-IN")}</td>
                  <td className="px-5 py-4">{statusPill(r.status)}</td>
                  <td className="px-5 py-4 text-right">
                    {r.status === "confirmed" || r.status === "pending" ? (
                      <button onClick={() => checkIn(r.id)} className="text-xs text-[#D4AF37] hover:text-[#F2D06B] inline-flex items-center gap-1" data-testid={`check-in-${r.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Check in
                      </button>
                    ) : r.status === "checked_in" ? (
                      <button onClick={() => checkOut(r.id)} className="text-xs text-white/70 hover:text-[#D4AF37] inline-flex items-center gap-1" data-testid={`check-out-${r.id}`}>
                        <LogOutIcon className="w-3.5 h-3.5" /> Check out
                      </button>
                    ) : <span className="text-xs text-white/30">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
